// The record axis of a trace tab, as transitions over the tab object
// ScopedPage keeps in openTabs. The axis is the conversation record read
// newest-first in pages: getTrace opens it on the newest page, every later one
// lands in front of the oldest turn shown. Kept pure so the tab's states can
// be walked without the page's hooks: first page, earlier page, a read to the
// end, and a rejected read left retryable. A whole conversation and one
// bounded at a request take the same transitions.

// What the next page read asks for, or null when there is nothing to read:
// no session data yet, or a record read to its end. A rejected read stays
// readable, so the control can retry it.
//
// The record is the SESSION's, whichever id the tab was opened on: a trace
// opened on the session reads it to its newest observation, one opened on a
// request reads it up to the request's own time (`record.untilMs`) -- a
// request is the conversation up to a moment -- and marks the messages that
// request shipped as its own (`focalEventId`).
export function sessionPageRequest(data) {
  if (!data || (data.record?.exhausted && !data.record?.rejected)) return null;
  return {
    sessionId: data.sessionId || data.id,
    cursor: data.record?.nextCursor ?? null,
    exclude: (data.turns || []).filter(isConversationTurn).map((t) => t.messageId).filter(Boolean),
    sinceMs: data.firstTimestamp ?? null,
    untilMs: Number.isFinite(data.record?.untilMs) ? data.record.untilMs : null,
    // A bounded axis is a request's: the tab's id is the request to mark.
    focalEventId: Number.isFinite(data.record?.untilMs) ? (data.id || null) : null,
  };
}

// A turn of the conversation proper, as opposed to the instructions a
// request carried in front of it (its system prompt, its tool catalog), which
// a request tab keeps at the head of its axis across every page.
const isConversationTurn = (turn) => !turn?.sectionKind || turn.sectionKind === "conversation";

export function beginSessionPage(tab) {
  return { ...tab, data: { ...tab.data, record: { ...tab.data.record, session: true, loading: true, rejected: false } } };
}

// The newest page opens the axis; every later one prepends. The count is
// read with the newest page only, so a cursor page without one keeps it. A
// request tab's instructions (system prompt, tool catalog) stay at the head
// of the axis: they are the request's, not the record's, and no page carries
// them.
export function applySessionPage(tab, page, { cursor }) {
  const prior = tab.data.turns || [];
  const instructions = prior.filter((t) => !isConversationTurn(t));
  const conversation = prior.filter(isConversationTurn);
  const turns = cursor ? [...instructions, ...page.turns, ...conversation] : [...instructions, ...page.turns];
  const sourceIds = new Set(turns.map(turn => turn.sourceEventId).filter(Boolean));
  const priorAttributions = cursor ? tab.data.clientAttributions : null;
  const clientAttributions = Array.isArray(page.clientAttributions) || Array.isArray(priorAttributions)
    ? [...new Map([...(priorAttributions || []), ...(page.clientAttributions || [])]
      .filter(entry => sourceIds.has(entry.sourceEventId))
      .map(entry => [entry.sourceEventId, entry])).values()]
    : null;
  return { ...tab, data: { ...tab.data, turns, clientAttributions,
    clientAttributionsTruncated: Boolean(page.clientAttributionsTruncated || (cursor && tab.data.clientAttributionsTruncated)), record: {
    ...(Number.isFinite(tab.data.record?.untilMs) ? { untilMs: tab.data.record.untilMs } : {}),
    session: true, pending: false, loading: false, rejected: false,
    loaded: turns.length - instructions.length,
    total: page.total ?? tab.data.record?.total ?? null,
    nextCursor: page.nextCursor ?? null,
    exhausted: Boolean(page.exhausted),
    pages: (tab.data.record?.pages || 0) + 1,
  } } };
}

// A failed read leaves the axis as it was and says so.
export function rejectSessionRecord(tab, reason) {
  return { ...tab, data: { ...tab.data, record: {
    ...tab.data.record, session: true, pending: false, loading: false, rejected: true, reason,
  } } };
}
